// Per-shape text formatting — defaults + merge + capability matrix.
//
// Why this exists: Miro-style text formatting (font size, bold/italic/underline,
// color, h/v align) on every text-bearing shape. The data lives in an optional
// `TextStyle` on the shape; this module computes the effective style by
// layering shape-type defaults under that override, and tells the UI which
// fields each shape type is allowed to expose.
//
// Sticky deliberately locks `color` to keep the paper-on-paper aesthetic
// intact — a custom text color on a tinted sticky undermines what stickies
// are for. Frame label has no `verticalAlign` because the label is a single
// line riding above the frame, not laid out inside a box.

import type { Shape, TextStyle } from '@frame/shared';
import { readableTextOn } from '../styles/contrast';
import type { CanvasTokens } from '../styles/tokens';

export type TextStyleField =
  | 'fontSize'
  | 'bold'
  | 'italic'
  | 'underline'
  | 'color'
  | 'align'
  | 'verticalAlign';

// Concrete (non-optional) text style — what the renderer / editor / toolbar
// actually consume after defaults have been applied.
export interface ResolvedTextStyle {
  fontSize: number;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  color: string;
  align: 'left' | 'center' | 'right';
  verticalAlign: 'top' | 'middle' | 'bottom';
}

// Whether the formatting toolbar should expose this control for this shape
// type. Keep this aligned with the toolbar's render logic — the toolbar is
// the only call site that needs the full matrix.
export function textStyleSupports(shape: Shape, field: TextStyleField): boolean {
  switch (shape.type) {
    case 'sticky':
      // Sticky paper aesthetic locks color + verticalAlign.
      return field !== 'color' && field !== 'verticalAlign';
    case 'rect':
    case 'ellipse':
      return true;
    case 'text':
      // Text shape is a single text block on the canvas — there's no box to
      // align inside vertically.
      return field !== 'verticalAlign';
    case 'frame':
      // Frame label rides outside the frame on a single baseline.
      return field !== 'verticalAlign';
    default:
      return false;
  }
}

// True if this shape type carries any text at all (and so the formatting
// toolbar applies). Mirrors `isTextEditable` from tools.ts; intentionally
// kept separate so this module stays pure / token-free at import time.
export function isTextBearing(shape: Shape): boolean {
  return (
    shape.type === 'sticky' ||
    shape.type === 'rect' ||
    shape.type === 'ellipse' ||
    shape.type === 'text' ||
    shape.type === 'frame'
  );
}

// Per-shape default text style. Color defaults are computed from the shape's
// own fill / theme so a freshly-created shape reads cleanly without the user
// having to pick a color.
export function defaultTextStyle(shape: Shape, tokens: CanvasTokens): ResolvedTextStyle {
  switch (shape.type) {
    case 'sticky':
      // Miro-style paper aesthetic: bigger centered text, both axes. The
      // align field is user-overridable; verticalAlign is locked per the
      // textStyleSupports() matrix.
      return {
        fontSize: 18,
        bold: false,
        italic: false,
        underline: false,
        color: tokens.stickyText,
        align: 'center',
        verticalAlign: 'middle',
      };
    case 'rect':
    case 'ellipse':
      return {
        fontSize: 14,
        bold: false,
        italic: false,
        underline: false,
        color: readableTextOn(shape.fill),
        align: 'center',
        verticalAlign: 'middle',
      };
    case 'text':
      return {
        fontSize: shape.fontSize ?? 16,
        bold: false,
        italic: false,
        underline: false,
        color: tokens.fgPrimary,
        align: 'left',
        verticalAlign: 'top',
      };
    case 'frame':
      return {
        fontSize: 12,
        bold: false,
        italic: false,
        underline: false,
        color: tokens.fgSecondary,
        align: 'left',
        verticalAlign: 'top',
      };
    default:
      return {
        fontSize: 14,
        bold: false,
        italic: false,
        underline: false,
        color: tokens.fgPrimary,
        align: 'left',
        verticalAlign: 'top',
      };
  }
}

// Effective text style for rendering — defaults overlaid with the shape's
// `textStyle` overrides, then disallowed fields stripped back to defaults so
// a stale override on a sticky can't sneak a custom color through.
export function effectiveTextStyle(shape: Shape, tokens: CanvasTokens): ResolvedTextStyle {
  const base = defaultTextStyle(shape, tokens);
  const override = readTextStyle(shape);
  if (!override) return base;
  const merged: ResolvedTextStyle = { ...base };
  if (override.fontSize !== undefined && textStyleSupports(shape, 'fontSize')) {
    merged.fontSize = override.fontSize;
  }
  if (override.bold !== undefined && textStyleSupports(shape, 'bold')) {
    merged.bold = override.bold;
  }
  if (override.italic !== undefined && textStyleSupports(shape, 'italic')) {
    merged.italic = override.italic;
  }
  if (override.underline !== undefined && textStyleSupports(shape, 'underline')) {
    merged.underline = override.underline;
  }
  if (override.color !== undefined && textStyleSupports(shape, 'color')) {
    merged.color = override.color;
  }
  if (override.align !== undefined && textStyleSupports(shape, 'align')) {
    merged.align = override.align;
  }
  if (override.verticalAlign !== undefined && textStyleSupports(shape, 'verticalAlign')) {
    merged.verticalAlign = override.verticalAlign;
  }
  return merged;
}

export function readTextStyle(shape: Shape): TextStyle | undefined {
  if (
    shape.type === 'sticky' ||
    shape.type === 'rect' ||
    shape.type === 'ellipse' ||
    shape.type === 'text' ||
    shape.type === 'frame'
  ) {
    return shape.textStyle;
  }
  return undefined;
}

// Konva accepts a single `fontStyle` string carrying both weight and italic.
export function konvaFontStyle(style: ResolvedTextStyle): string {
  if (style.bold && style.italic) return 'bold italic';
  if (style.bold) return 'bold';
  if (style.italic) return 'italic';
  return 'normal';
}

// Preset palette for the toolbar's color picker. Hand-picked monochrome +
// accents that pair with the design system; the custom hex input covers
// anything beyond these.
export const TEXT_COLOR_PRESETS: readonly string[] = [
  '#18181B', // ink
  '#52525B', // graphite
  '#71717A', // ash
  '#FFFFFF', // paper
  '#EF4444', // red
  '#F59E0B', // amber
  '#10B981', // emerald
  '#06B6D4', // cyan
  '#3B82F6', // blue
  '#8B5CF6', // violet
  '#EC4899', // pink
  '#A16207', // ochre
];

// Font-size constraints for the toolbar's stepper.
export const MIN_FONT_SIZE = 8;
export const MAX_FONT_SIZE = 144;

export function clampFontSize(size: number): number {
  if (!Number.isFinite(size)) return MIN_FONT_SIZE;
  return Math.max(MIN_FONT_SIZE, Math.min(MAX_FONT_SIZE, Math.round(size)));
}

// Cycle helpers used by the toolbar's align/vAlign buttons.
export function nextAlign(curr: ResolvedTextStyle['align']): ResolvedTextStyle['align'] {
  if (curr === 'left') return 'center';
  if (curr === 'center') return 'right';
  return 'left';
}

export function nextVerticalAlign(
  curr: ResolvedTextStyle['verticalAlign'],
): ResolvedTextStyle['verticalAlign'] {
  if (curr === 'top') return 'middle';
  if (curr === 'middle') return 'bottom';
  return 'top';
}

// Patch a shape's textStyle by merging a single field, preserving the rest.
// Returns the value to hand to `updateShape({ textStyle: ... })`.
export function patchTextStyle(
  shape: Shape,
  field: TextStyleField,
  value: TextStyle[TextStyleField],
): TextStyle {
  const current = readTextStyle(shape) ?? {};
  return { ...current, [field]: value };
}
